import { marked } from "marked";

// Configuração global do marked (GFM + quebras de linha simples).
marked.setOptions({ gfm: true, breaks: true });

// Rótulo amigável de cada rota interna (para transformar caminhos em botões).
const ROUTE_LABELS: Record<string, string> = {
	"/dashboard": "Início",
	"/consultants": "Consulentes",
	"/mediums": "Corrente",
	"/journey-stages": "Etapas da Jornada",
	"/functions": "Funções",
	"/spiritual-lines": "Linhas Espirituais",
	"/orixas": "Orixás",
	"/guides": "Guias",
	"/ritual-types": "Tipos de Ritual",
	"/attendance-types": "Tipos de Atendimento",
	"/rituals": "Rituais",
	"/member-groups": "Grupos",
	"/financial/funds": "Fundos",
	"/financial/movements": "Movimentações",
	"/financial/reports": "Fechamento",
	"/financial/integrations": "Pagamentos Online",
	"/financial": "Painel Financeiro",
	"/temple/plan": "Meu Plano",
	"/temple/settings": "Configurações do Templo",
	"/temple/invites": "Convites",
	"/temple/users": "Usuários",
	"/temple/import": "Importação",
	"/support": "Falar com o Suporte",
	"/profile": "Meu Perfil",
	"/pricing": "Planos",
	"/onboarding": "Primeiros passos",
};

// Alternância de rotas (mais longas primeiro para casar /financial/reports antes de /financial).
const ROUTE_ALT = Object.keys(ROUTE_LABELS)
	.sort((a, b) => b.length - a.length)
	.map((r) => r.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
	.join("|");
const EXISTING_LINK_RE = /\[[^\]]*\]\([^)]*\)/g;
// Sem lookbehind (compatível com todos os navegadores):
// 1) caminho entre parênteses: (/rota)
const PAREN_ROUTE_RE = new RegExp(`\\(\\s*(${ROUTE_ALT})\\s*\\)`, "g");
// 2) caminho solto com fronteira à esquerda (início, espaço ou '>')
const BARE_ROUTE_RE = new RegExp(`(^|[\\s>])(${ROUTE_ALT})(?![\\w/-])`, "g");

const toButton = (route: string): string =>
	`[${ROUTE_LABELS[route] ?? route}](${route})`;

const transformBare = (text: string): string =>
	text
		.replace(PAREN_ROUTE_RE, (_f, r) => toButton(r))
		.replace(BARE_ROUTE_RE, (_f, pre, r) => `${pre}${toButton(r)}`);

/** Converte caminhos internos "soltos" em links markdown, sem tocar em links já existentes. */
export const linkifyRoutes = (raw: string): string => {
	// Remove parênteses que apenas envolvem um link: ([Rótulo](/rota)) -> [Rótulo](/rota)
	const src = raw.replace(/\(\s*(\[[^\]]*\]\([^)]*\))\s*\)/g, "$1");
	let out = "";
	let last = 0;
	for (const m of src.matchAll(EXISTING_LINK_RE)) {
		const idx = m.index ?? 0;
		out += transformBare(src.slice(last, idx));
		out += m[0]; // mantém link já existente intacto
		last = idx + m[0].length;
	}
	out += transformBare(src.slice(last));
	return out;
};

// Tags permitidas na renderização do markdown do bot.
const ALLOWED_TAGS = new Set([
	"A", "P", "BR", "STRONG", "B", "EM", "I", "U", "S", "DEL",
	"UL", "OL", "LI", "CODE", "PRE", "BLOCKQUOTE", "HR", "SPAN",
	"H1", "H2", "H3", "H4", "H5", "H6",
	"TABLE", "THEAD", "TBODY", "TR", "TH", "TD",
]);
const DROP_TAGS = new Set([
	"SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED",
	"SVG", "MATH", "NOSCRIPT", "TEMPLATE", "FORM",
	"LINK", "META", "BASE", "TITLE", "IMG", "AUDIO", "VIDEO", "SOURCE",
]);
// href seguro: http(s) absoluto, mailto, âncora, ou caminho interno de UMA barra
// (rejeita protocolo-relativo "//" e "/\" → evita open redirect/phishing).
const SAFE_HREF_RE = /^(https?:\/\/|mailto:|#|\/(?![/\\]))/i;
export const isInternalHref = (href: string): boolean =>
	href.startsWith("#") || /^\/(?![/\\])/.test(href);

/** Sanitiza HTML por allowlist usando o DOM (output de LLM é não confiável). */
export const sanitize = (html: string): string => {
	const tpl = document.createElement("template");
	tpl.innerHTML = html;
	for (const el of Array.from(tpl.content.querySelectorAll("*"))) {
		const tag = el.tagName;
		if (!ALLOWED_TAGS.has(tag)) {
			if (DROP_TAGS.has(tag)) el.remove();
			else el.replaceWith(...Array.from(el.childNodes)); // desembrulha tag desconhecida
			continue;
		}
		for (const attr of Array.from(el.attributes)) {
			const name = attr.name.toLowerCase();
			const safeHref =
				tag === "A" &&
				name === "href" &&
				SAFE_HREF_RE.test(attr.value.trim());
			if (!safeHref) el.removeAttribute(attr.name);
		}
		if (tag === "A") {
			const href = el.getAttribute("href");
			if (!href) {
				// href removido por ser inseguro → vira texto simples
				el.replaceWith(...Array.from(el.childNodes));
				continue;
			}
			// Links viram botões; internos (rota do app) navegam pelo router.
			el.setAttribute("class", "chat-link-btn");
			const internal = isInternalHref(href);
			if (!internal) {
				el.setAttribute("target", "_blank");
				el.setAttribute("rel", "noopener noreferrer nofollow");
			}
		}
	}
	return tpl.innerHTML;
};

export const renderMarkdown = (content: string): string =>
	sanitize(marked.parse(linkifyRoutes(content), { async: false }) as string);
